/* iter103 — School group messages shown in the parent portal, with reply. */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Megaphone, PaperPlaneTilt, ChatCircleDots } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../lib/api';

const fmtDate = (iso) => { const d = new Date(iso); return isNaN(d) ? iso : d.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }); };

const ParentBroadcasts = ({ isFR = true }) => {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState('');
  const [expanded, setExpanded] = useState(false);

  const load = () => api.get('/parent/broadcasts').then(r => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const reply = async (b) => {
    const text = (drafts[b.batch_id] || '').trim();
    if (!text) return;
    setBusy(b.batch_id);
    try {
      await api.post(`/parent/broadcasts/${b.batch_id}/reply`, { message: text, student_id: b.messages[0]?.student_id });
      toast.success(isFR ? 'Réponse envoyée à l\'école' : 'Reply sent');
      setDrafts(d => ({ ...d, [b.batch_id]: '' }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || (isFR ? 'Erreur' : 'Error')); }
    finally { setBusy(''); }
  };

  if (!items.length) return null;
  const shown = expanded ? items : items.slice(0, 3);

  return (
    <Card className="border border-[#E2E8F0] shadow-none" data-testid="parent-broadcasts-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-outfit text-base flex items-center gap-2">
          <Megaphone size={18} weight="duotone" className="text-[#0052FF]" />
          {isFR ? "Messages de l'école" : 'School messages'}
          <span className="text-xs font-normal text-[#94A3B8]">({items.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {shown.map(b => (
          <div key={b.batch_id} className="rounded-md border border-[#E2E8F0] p-3 space-y-2" data-testid={`parent-broadcast-${b.batch_id}`}>
            <div className="flex items-center justify-between text-xs text-[#64748B]">
              <b className="text-[#0F172A]">{b.title}</b>
              <span>{fmtDate(b.created_at)}</span>
            </div>
            {b.messages.map(m => (
              <div key={m.student_id} className="text-sm text-[#0F172A] whitespace-pre-wrap">
                {b.messages.length > 1 && <span className="text-xs text-[#64748B] mr-1">{m.student_name} :</span>}
                {m.text}
              </div>
            ))}
            {b.replies.length > 0 && (
              <div className="space-y-1 border-l-2 border-indigo-200 pl-2">
                {b.replies.map(r => (
                  <div key={r.id} className={`text-xs ${r.direction === 'school' ? 'text-emerald-800 bg-emerald-50 rounded px-1.5 py-1' : 'text-[#475569]'}`}
                    data-testid={r.direction === 'school' ? `parent-school-answer-${r.id}` : `parent-reply-${r.id}`}>
                    <ChatCircleDots size={12} className={`inline mr-1 ${r.direction === 'school' ? 'text-emerald-600' : 'text-indigo-600'}`} />
                    {r.direction === 'school' && <b>{isFR ? "École" : 'School'} · </b>}
                    <span className="text-[#94A3B8]">{fmtDate(r.created_at)} · </span>{r.message}
                    {r.direction !== 'school' && r.read && <span className="ml-1 text-emerald-600">✓ {isFR ? 'lu' : 'read'}</span>}
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input value={drafts[b.batch_id] || ''} onChange={e => setDrafts(d => ({ ...d, [b.batch_id]: e.target.value.slice(0, 500) }))}
                placeholder={isFR ? 'Répondre à l\'école…' : 'Reply to the school…'}
                data-testid={`parent-reply-input-${b.batch_id}`}
                className="flex-1 rounded-md border border-[#E2E8F0] px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF]/40" />
              <Button size="sm" onClick={() => reply(b)} disabled={busy === b.batch_id || !(drafts[b.batch_id] || '').trim()}
                data-testid={`parent-reply-send-${b.batch_id}`} className="bg-[#0052FF] hover:bg-[#0041CC] text-white">
                <PaperPlaneTilt size={14} />
              </Button>
            </div>
          </div>
        ))}
        {items.length > 3 && (
          <Button variant="ghost" size="sm" onClick={() => setExpanded(v => !v)} data-testid="parent-broadcasts-toggle" className="text-xs">
            {expanded ? (isFR ? 'Voir moins' : 'Show less') : (isFR ? `Voir les ${items.length} messages` : `Show all ${items.length}`)}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ParentBroadcasts;
